import { useCallback, useState } from 'react'

import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  formatUuidToNodeType,
  getChildNodeType,
  getTextFromNodeType,
} from 'modules/job_catalog/lib/nodesUtils'

import useCreateJobCatalogTreeNode from './useCreateJobCatalogTreeNode'

interface CreateTreeParams {
  name: string
  description: string
  children?: string[]
  parentUuid: string
  nodeType: string
  companyId: string
}

interface CreateTreeResult {
  success: boolean
  newNodeUuid?: string
  error?: string
}

const useCreateTreeWithChildren = () => {
  const { createNode, loading } = useCreateJobCatalogTreeNode()
  const [error, setError] = useState<string | null>(null)

  const createTree = useCallback(
    async ({
      name,
      description,
      children = [],
      parentUuid,
      nodeType,
      companyId,
    }: CreateTreeParams): Promise<CreateTreeResult> => {
      setError(null)

      const [_, parentIdStr] = parentUuid.split('-')
      const parentId = parentIdStr ?? ''

      try {
        const parentResult = await createNode(
          companyId,
          description,
          name,
          nodeType,
          parentId,
          parentUuid
        )

        const handled = handleResult(parentResult, (_errors) => {})

        const newNodeId = handled.success ? handled.data?.treeNode?.id : null

        if (newNodeId && children.length > 0) {
          const childNodeType = formatUuidToNodeType(getChildNodeType(nodeType) || '')
          await Promise.all(
            children.map((childName) =>
              createNode(companyId, '', childName, childNodeType || '', newNodeId, parentUuid)
            )
          )
        }

        app.setFlash({
          type: 'correct',
          message: i18n.t('job_catalog.nodes.modal.create_success', {
            type: getTextFromNodeType(nodeType),
          }),
          time: 5000,
        })

        return {
          success: true,
          newNodeUuid: `${nodeType}-${newNodeId}`,
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : i18n.t('errors.generic_error')
        app.setFlash({
          type: 'wrong',
          message,
        })
        setError(message)
        return { success: false, error: message }
      }
    },
    [createNode]
  )

  return {
    createTree,
    loading,
    error,
  }
}

export default useCreateTreeWithChildren
